import React, { useState } from 'react';
import ImageUpload from '../ImageUpload';
import {
    addService,
    updateService,
    deleteService
} from '../../services/dataService';

const ServicesSection = ({ siteData, setSiteData, saving, setSaving, showSaveStatus }) => {
    const [editingServices, setEditingServices] = useState({});
    const [savingStates, setSavingStates] = useState({});

    const handleLocalUpdate = (serviceId, field, value) => {
        setEditingServices(prev => ({
            ...prev,
            [serviceId]: {
                ...(prev[serviceId] || siteData.services.find(s => s.id === serviceId)),
                [field]: value,
                hasChanges: true
            }
        }));
    };

    const handleImageUpload = async (serviceId, imageUrl, imagePath) => {
        try {
            const serviceToUpdate = siteData.services.find(s => s.id === serviceId);
            const updatedService = {
                ...serviceToUpdate,
                image: imageUrl,
                imagePath: imagePath
            };

            await updateService(String(serviceId), updatedService);

            const updatedServices = siteData.services.map(service =>
                service.id === serviceId ? { ...updatedService, id: serviceId } : service
            );
            setSiteData(prev => ({ ...prev, services: updatedServices }));

            showSaveStatus('Service image updated successfully!');
        } catch (error) {
            console.error('Failed to update service image:', error);
            showSaveStatus('Failed to update service image', true);
        }
    };

    const handleSaveService = async (serviceId) => {
        try {
            setSavingStates(prev => ({ ...prev, [serviceId]: true }));
            const updatedService = editingServices[serviceId];

            // Ensure serviceId is a string
            await updateService(String(serviceId), updatedService);

            const updatedServices = siteData.services.map(service =>
                service.id === serviceId ? { ...updatedService, id: serviceId } : service
            );
            setSiteData(prev => ({ ...prev, services: updatedServices }));

            // Clear editing state
            setEditingServices(prev => {
                const newState = { ...prev };
                delete newState[serviceId];
                return newState;
            });

            showSaveStatus('Service updated successfully!');
        } catch (error) {
            showSaveStatus('Failed to update service', true);
        } finally {
            setSavingStates(prev => ({ ...prev, [serviceId]: false }));
        }
    };

    const handleAddService = async () => {
        try {
            setSaving(true);
            const newService = {
                title: 'New Service',
                description: 'Service description',
                icon: '💼'
            };

            const serviceId = await addService(newService);
            const serviceWithId = { ...newService, id: serviceId };

            setSiteData(prev => ({
                ...prev,
                services: [...prev.services, serviceWithId]
            }));
            showSaveStatus('Service added successfully!');
        } catch (error) {
            showSaveStatus('Failed to add service', true);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteService = async (serviceId) => {
        if (!window.confirm('Are you sure you want to delete this service?')) {
            return;
        }

        try {
            setSaving(true);
            // Ensure serviceId is a string
            const serviceIdString = String(serviceId);
            await deleteService(serviceIdString);

            // Filter using string comparison to ensure proper matching
            const updatedServices = siteData.services.filter(service =>
                String(service.id) !== serviceIdString
            );
            setSiteData(prev => ({ ...prev, services: updatedServices }));

            // Clear editing state if exists
            setEditingServices(prev => {
                const newState = { ...prev };
                delete newState[serviceId];
                return newState;
            });

            showSaveStatus('Service deleted successfully!');
        } catch (error) {
            showSaveStatus('Failed to delete service', true);
        } finally {
            setSaving(false);
        }
    };

    const getServiceData = (serviceId) => {
        return editingServices[serviceId] || siteData.services.find(s => s.id === serviceId);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-stone-800">Services</h3>
                <button
                    onClick={handleAddService}
                    disabled={saving}
                    className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
                >
                    {saving ? 'Adding...' : 'Add Service'}
                </button>
            </div>

            <div className="space-y-4">
                {siteData.services.map((service) => {
                    const currentService = getServiceData(service.id);
                    const hasChanges = editingServices[service.id]?.hasChanges;
                    const isSaving = savingStates[service.id];

                    return (
                        <div key={service.id} className="bg-stone-50 p-4 rounded-lg">
                            <div className="flex justify-between items-start mb-4">
                                <h4 className="font-medium text-stone-800">Service: {currentService.title}</h4>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleSaveService(service.id)}
                                        disabled={!hasChanges || isSaving}
                                        className={`px-4 py-1 rounded transition-colors text-sm ${hasChanges && !isSaving
                                            ? 'bg-amber-500 text-white hover:bg-amber-600'
                                            : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                                            }`}
                                    >
                                        {isSaving ? 'Saving...' : hasChanges ? 'Save' : 'Saved'}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteService(service.id)}
                                        disabled={saving}
                                        className="text-red-500 hover:text-red-700 disabled:opacity-50 px-2"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={currentService.title}
                                        onChange={(e) => handleLocalUpdate(service.id, 'title', e.target.value)}
                                        className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">Icon (emoji)</label>
                                    <input
                                        type="text"
                                        value={currentService.icon}
                                        onChange={(e) => handleLocalUpdate(service.id, 'icon', e.target.value)}
                                        className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">
                                        Duration <span className="text-stone-400">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={currentService.duration || ''}
                                        onChange={(e) => handleLocalUpdate(service.id, 'duration', e.target.value)}
                                        placeholder="e.g., 60 minutes"
                                        className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">
                                        Price <span className="text-stone-400">(optional)</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={currentService.price || ''}
                                        onChange={(e) => handleLocalUpdate(service.id, 'price', e.target.value)}
                                        placeholder="e.g., 150"
                                        className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    />
                                </div>
                            </div>

                            <p className="text-xs text-stone-500 mt-2">
                                💡 Duration and Price are optional. If provided, they will be displayed on the service card with icons.
                            </p>

                            <div className="mt-4">
                                <label className="block text-sm font-medium text-stone-700 mb-1">Description</label>
                                <textarea
                                    value={currentService.description}
                                    onChange={(e) => handleLocalUpdate(service.id, 'description', e.target.value)}
                                    className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    rows="2"
                                />
                            </div>

                            <div className="mt-4">
                                <label className="block text-sm font-medium text-stone-700 mb-2">Service Image</label>
                                <p className="text-xs text-stone-500 mb-2">Upload an image for this service (optional - will use icon if no image)</p>
                                <ImageUpload
                                    currentImage={currentService.image}
                                    currentImagePath={currentService.imagePath}
                                    onImageUpload={(imageUrl, imagePath) => handleImageUpload(service.id, imageUrl, imagePath)}
                                    label="Upload Service Image"
                                    folder={`services/${service.id}`}
                                    enableCrop={true}
                                    aspectRatio={16 / 9}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ServicesSection;